import React from 'react';
import { Link } from 'react-router-dom';
export type BlogStep = {
  number: number;
  title: string;
  image: string;
  description: string;
  tips: string[];
  material?: string;
  materialSlug?: string;
};
export type BlogPost = {
  slug: string;
  title: string;
  date: string;
  excerpt: string;
  image: string;
  type: 'guide' | 'article';
  quickInfo?: string[];
  materialsNeeded?: {
    name: string;
    use: string;
    slug: string | null;
  }[];
  steps?: BlogStep[];
  content?: React.ReactNode;
  seoBlocks?: {
    title: string;
    content: React.ReactNode;
  }[];
};
export const blogPosts: BlogPost[] = [{
  slug: '5-8-minus-gravel-guide',
  title: '5/8" Minus Gravel: Weight, Coverage, and How to Install It',
  date: 'August 7, 2026',
  excerpt: 'It is the single most-ordered material we haul in King and Pierce counties. Here is what a yard weighs, how far it goes, where it belongs, and the install mistakes that cost people a second delivery.',
  image: 'https://cdn.magicpatterns.com/uploads/r4cKQMZPXB8R1pGgK5XvGj/5-8-minus-newcastle.png',
  type: 'article',
  content: <div className="prose dark:prose-invert max-w-none text-brand-charcoal dark:text-stone-300" data-id="element-1930" data-mp="2ik326jyrzvws">
        <p className="mb-4" data-id="element-1931" data-mp="24kbpqnnwqnkw">
          More than a third of the loads we dispatch on a given week are 5/8"
          Minus. Contractors order it by the truck without thinking twice.
          Homeowners call and describe it as "the gravel that packs down hard,"
          which is exactly right — that is the whole point of the material.
        </p>
        <p className="mb-4" data-id="element-1932" data-mp="326r7lqk8xn8z">
          <strong data-id="element-1933" data-mp="2nio5is9bqvpg">The short version:</strong> 5/8" Minus is crushed rock
          screened so nothing is larger than 5/8 of an inch, with all the fines
          from crushing left in. Those fines pack into the gaps between the
          angular pieces and lock the whole layer into a near-solid slab. One
          cubic yard weighs about 2,800 lbs and covers roughly 100 sq ft at 3
          inches deep. It runs $65 per yard delivered from our yard, plus a flat
          haul fee by zone.
        </p>

        <img src="https://cdn.magicpatterns.com/uploads/uJHdEMdk8Ku5nMisWk411s/58minus.png" alt="Close-up of 5/8 inch minus crushed gravel showing angular stone mixed with fines" className="w-full h-64 md:h-80 object-cover rounded-xl my-6" data-id="element-1934" data-mp="3pv7d6ycya99r" />

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-1935" data-mp="3gtho3z6jjmf5">
          What "Minus" Actually Means
        </h3>
        <p className="mb-4" data-id="element-1936" data-mp="3bwzh0snqnbzn">
          Take quarry rock, run it through a crusher, then screen out everything
          bigger than 5/8 of an inch — about the width of a nickel. What is left
          is every size below that, down to the rock dust the crusher produced.
          The dust is the "minus," and it is the reason the material works.
        </p>
        <p className="mb-4" data-id="element-1937" data-mp="2t3dl9u16fqp7">
          Run a compactor over it and those fines migrate into every void
          between the larger angular chips. The layer stops behaving like loose
          stone and starts behaving like a slab. Clean rock — the same stone with
          the fines washed out — never does that. It stays loose forever, which
          is excellent if you want water to pour through it and useless if you
          want to drive on it.
        </p>
        <p className="mb-4" data-id="element-1938" data-mp="1hnud604nauem">
          One rule covers almost every ordering decision:{' '}
          <strong data-id="element-1939" data-mp="kxlfslf6rurx">
            if it needs to be solid, order a minus. If it needs to drain, order
            a clean.
          </strong>{' '}
          Getting that backwards is the single most common mistake we see, and
          the consequences show up within one wet season.
        </p>
        <div className="overflow-x-auto my-6" data-id="element-1940" data-mp="yo6ppkmnwudo">
          <table className="w-full text-left text-sm [&_td]:pl-3 [&_th]:pl-3" data-id="element-1941" data-mp="yq1b101uioia">
            <thead data-id="element-1942" data-mp="181a3jueb2o4">
              <tr className="border-b border-stone-300 dark:border-white/20" data-id="element-1943" data-mp="18jbeuvx072w">
                <th className="py-2 pr-4 font-bold" data-id="element-1944" data-mp="67450bgnvsid" />
                <th className="py-2 pr-4 font-bold" data-id="element-1945" data-mp="28uu9iha21aj4">5/8" Minus</th>
                <th className="py-2 font-bold" data-id="element-1946" data-mp="28vchuc9zrf7e">5/8" Clean</th>
              </tr>
            </thead>
            <tbody className="[&>tr:nth-child(even)]:bg-stone-500/10 dark:[&>tr:nth-child(even)]:bg-white/[0.06]" data-id="element-1947" data-mp="23xau3e1merpl">
              <tr className="border-b border-stone-200 dark:border-white/10" data-id="element-1948" data-mp="2hp903g01c7hj">
                <td className="py-2 pr-4 font-semibold" data-id="element-1949" data-mp="2cuv1z5tznm96">Contains fines?</td>
                <td className="py-2 pr-4" data-id="element-1950" data-mp="2rk9akbpqpzni">Yes — dust left in</td>
                <td className="py-2" data-id="element-1951" data-mp="2mm7p5k4udo8v">No — washed out</td>
              </tr>
              <tr className="border-b border-stone-200 dark:border-white/10" data-id="element-1952" data-mp="2mobs9zux2j83">
                <td className="py-2 pr-4 font-semibold" data-id="element-1953" data-mp="30lgwrvezfa18">Compacts?</td>
                <td className="py-2 pr-4" data-id="element-1954" data-mp="2wk5fqea6bx4n">Yes, locks up hard</td>
                <td className="py-2" data-id="element-1955" data-mp="167zhswsn7lht">No, stays loose</td>
              </tr>
              <tr className="border-b border-stone-200 dark:border-white/10" data-id="element-1956" data-mp="16895gdq7tgkc">
                <td className="py-2 pr-4 font-semibold" data-id="element-1957" data-mp="127p93cdgugz9">Water passes through?</td>
                <td className="py-2 pr-4" data-id="element-1958" data-mp="s3iczoufuasp">Barely — fines seal it</td>
                <td className="py-2" data-id="element-1959" data-mp="w3su9xnoknmv">Freely</td>
              </tr>
              <tr data-id="element-1960" data-mp="w39dwldoi7ay">
                <td className="py-2 pr-4 font-semibold" data-id="element-1961" data-mp="w5vl2jwnqksn">Order it for</td>
                <td className="py-2 pr-4" data-id="element-1962" data-mp="icdyeq8ocrye">
                  Driveways, paths, base, mud control
                </td>
                <td className="py-2" data-id="element-1963" data-mp="n9e3oyxp9qog">
                  French drains, pipe bedding, dry creek beds
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-1964" data-mp="dbggmerktzxf">
          What a Yard Weighs — and Why Your Pickup Cannot Carry One
        </h3>
        <p className="mb-4" data-id="element-1965" data-mp="4do0j8e16o8z">
          A cubic yard of 5/8" Minus runs about 2,800 lbs, roughly 1.4 tons.
          Moisture moves that number a little; a yard that has been sitting in
          November rain is meaningfully heavier than the same yard in August.
        </p>
        <div className="overflow-x-auto my-6" data-id="element-1966" data-mp="1wcqwhgyqtg2g">
          <table className="w-full text-left text-sm [&_td]:pl-3 [&_th]:pl-3" data-id="element-1967" data-mp="1wdk1ace55vhy">
            <thead data-id="element-1968" data-mp="1s9uensh11nzk">
              <tr className="border-b border-stone-300 dark:border-white/20" data-id="element-1969" data-mp="1scqgvszwqytt">
                <th className="py-2 pr-4 font-bold" data-id="element-1970" data-mp="1nc2r1anf0o16">Quantity</th>
                <th className="py-2 pr-4 font-bold" data-id="element-1971" data-mp="1iczivhc521oz">Approx. weight</th>
                <th className="py-2 font-bold" data-id="element-1972" data-mp="1iespwxqnynxi">Typical job</th>
              </tr>
            </thead>
            <tbody className="[&>tr:nth-child(even)]:bg-stone-500/10 dark:[&>tr:nth-child(even)]:bg-white/[0.06]" data-id="element-1973" data-mp="2b156cbuku4si">
              <tr className="border-b border-stone-200 dark:border-white/10" data-id="element-1974" data-mp="26a5bdc50pwsn">
                <td className="py-2 pr-4" data-id="element-1975" data-mp="2682b7za5ina7">1 yard</td>
                <td className="py-2 pr-4" data-id="element-1976" data-mp="21aiwky9jk8hq">~2,800 lbs</td>
                <td className="py-2" data-id="element-1977" data-mp="2yqpt75tupm72">Patching a few potholes</td>
              </tr>
              <tr className="border-b border-stone-200 dark:border-white/10" data-id="element-1978" data-mp="2ysj08m8dm8fl">
                <td className="py-2 pr-4" data-id="element-1979" data-mp="2tyohbl0lhxhk">4 yards</td>
                <td className="py-2 pr-4" data-id="element-1980" data-mp="31bntiqjvzeey">~5.6 tons</td>
                <td className="py-2" data-id="element-1981" data-mp="31d8nadydmtbr">Topping off a short driveway</td>
              </tr>
              <tr className="border-b border-stone-200 dark:border-white/10" data-id="element-1982" data-mp="36ah3wa3vmpaw">
                <td className="py-2 pr-4" data-id="element-1983" data-mp="3b8ipb1orz0pj">12 yards (full load)</td>
                <td className="py-2 pr-4" data-id="element-1984" data-mp="3g8xzxlho9u3t">~16.8 tons</td>
                <td className="py-2" data-id="element-1985" data-mp="3g6bsdlus7ell">New driveway, ~10 × 50 ft at 6"</td>
              </tr>
              <tr data-id="element-1986" data-mp="3k9q9ygydc315">
                <td className="py-2 pr-4" data-id="element-1987" data-mp="3k85pjk5rq0h0">Half-ton pickup</td>
                <td className="py-2 pr-4" data-id="element-1988" data-mp="3p7s1lq9v1ao8">1,000–1,500 lbs</td>
                <td className="py-2" data-id="element-1989" data-mp="3u02zyooycoeo">
                  About 0.4 yd — <strong data-id="element-1990" data-mp="3u0l74gd5kn1d">not</strong> a full yard
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="mb-4" data-id="element-1991" data-mp="7sv2e8k9xlt7">
          That last row catches people out constantly. A half-ton pickup is
          named for its payload rating, not its curb weight, and a single yard of
          minus is nearly double that rating. Loading a full yard into a
          half-ton overloads the suspension, ruins your braking distance, and is
          illegal on public roads. If you need one yard, take two trips, borrow a
          three-quarter ton, or have it delivered — our{' '}
          <Link to="/shop/5-8-minus" className="underline underline-offset-2" data-id="element-1992" data-mp="170yltp7sqiws">
            5/8" Minus page
          </Link>{' '}
          will quote the delivered price to your address before you commit.
        </p>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-1993" data-mp="9fubmx80xb53">
          How Far a Yard Goes
        </h3>
        <p className="mb-4" data-id="element-1994" data-mp="o5i9fogzxro2">
          Coverage is entirely a function of depth. These are the numbers we use
          on the phone when someone reads us their dimensions:
        </p>
        <div className="overflow-x-auto my-6" data-id="element-1995" data-mp="3rss30fqr2vnn">
          <table className="w-full text-left text-sm [&_td]:pl-3 [&_th]:pl-3" data-id="element-1996" data-mp="3rrgz8fxb1nwj">
            <thead data-id="element-1997" data-mp="3mtxmjkg99j6p">
              <tr className="border-b border-stone-300 dark:border-white/20" data-id="element-1998" data-mp="3mscsdvxus29d">
                <th className="py-2 pr-4 font-bold" data-id="element-1999" data-mp="22avbar0dgdjh">Depth</th>
                <th className="py-2 pr-4 font-bold" data-id="element-2000" data-mp="273elrf3risk2">Coverage per yard</th>
                <th className="py-2 font-bold" data-id="element-2001" data-mp="1tbqcrkot8k7b">Use this depth for</th>
              </tr>
            </thead>
            <tbody className="[&>tr:nth-child(even)]:bg-stone-500/10 dark:[&>tr:nth-child(even)]:bg-white/[0.06]" data-id="element-2002" data-mp="1xd23jtjf7bh3">
              <tr className="border-b border-stone-200 dark:border-white/10" data-id="element-2003" data-mp="2l2o1yeryxmo1">
                <td className="py-2 pr-4" data-id="element-2004" data-mp="2q1qw346eu8h8">2"</td>
                <td className="py-2 pr-4" data-id="element-2005" data-mp="2q1h4jc5pvt9f">~160 sq ft</td>
                <td className="py-2" data-id="element-2006" data-mp="2c2hp8v43gqpx">Refreshing an existing surface</td>
              </tr>
              <tr className="border-b border-stone-200 dark:border-white/10" data-id="element-2007" data-mp="2h1axo94ixrj8">
                <td className="py-2 pr-4" data-id="element-2008" data-mp="2h3f5h25jnaos">3"</td>
                <td className="py-2 pr-4" data-id="element-2009" data-mp="10mt1i1j0nno2">~100 sq ft</td>
                <td className="py-2" data-id="element-2010" data-mp="10pfam5lk2b8e">Paths, mud control, top layer</td>
              </tr>
              <tr className="border-b border-stone-200 dark:border-white/10" data-id="element-2011" data-mp="15k2qxh6enzbw">
                <td className="py-2 pr-4" data-id="element-2012" data-mp="6ivi37q3pmoh">4"</td>
                <td className="py-2 pr-4" data-id="element-2013" data-mp="ajp6013jn1hd">~80 sq ft</td>
                <td className="py-2" data-id="element-2014" data-mp="aj5lqdqf8106">Light-duty driveway over solid base</td>
              </tr>
              <tr data-id="element-2015" data-mp="3sxobjwyl1olv">
                <td className="py-2 pr-4" data-id="element-2016" data-mp="3sw3sp4lmru3u">6"</td>
                <td className="py-2 pr-4" data-id="element-2017" data-mp="1qljz4eu1gb4">~54 sq ft</td>
                <td className="py-2" data-id="element-2018" data-mp="1pak3foicspa">New driveway on soft ground</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="mb-4" data-id="element-2019" data-mp="3nz3lgqd1olb5">
          Add about 15% to whatever the math gives you. Compaction takes real
          volume out of a loose layer, and being a yard short at 4pm on a
          Saturday is a far worse outcome than having a wheelbarrow of rock left
          over. Our{' '}
          <Link to="/calculator" className="underline underline-offset-2" data-id="element-2020" data-mp="187619f4dezqc">
            yardage calculator
          </Link>{' '}
          already builds that allowance in.
        </p>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2021" data-mp="1naqp4vt1g8iw">
          Where It Belongs
        </h3>
        <ul className="list-disc pl-5 mb-4 space-y-2" data-id="element-2022" data-mp="1icffal2qty0o">
          <li data-id="element-2023" data-mp="1i9t6ki449cgv">
            <strong data-id="element-2024" data-mp="1w2slahw2r2nd">Residential driveways.</strong> Its main job. Four to six
            inches, crowned and compacted, handles daily car and light-truck
            traffic for years before it wants a fresh top layer.
          </li>
          <li data-id="element-2025" data-mp="2605omlt2spl6">
            <strong data-id="element-2026" data-mp="25znj0ykiwz0l">Paths and walkways.</strong> A firm walking surface for a
            fraction of what pavers cost. Three inches over fabric is plenty.
          </li>
          <li data-id="element-2027" data-mp="2f26kbe8nv3kq">
            <strong data-id="element-2028" data-mp="2f0lo7k6p7ckr">Base under pavers, slabs, and walls.</strong> Compacts into
            something that carries weight evenly and does not settle the way
            soil does. Skip it and a patio goes wavy inside two seasons.
          </li>
          <li data-id="element-2029" data-mp="kpp0yhqy0nld">
            <strong data-id="element-2030" data-mp="gmjzmiilmryb">Mud control.</strong> Gate areas, dog runs, side-yard
            parking. Three to four inches over fabric turns October-to-May soup
            into ground you can walk on in normal boots.
          </li>
          <li data-id="element-2031" data-mp="2kpbwdlsklacl">
            <strong data-id="element-2032" data-mp="2kqwq59728p9e">Backfill.</strong> Around foundations, behind retaining
            walls, over utility trenches. Compacts tight around irregular shapes
            without rocks large enough to damage pipe.
          </li>
        </ul>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2033" data-mp="2ulyei2tihnax">
          Where It Does Not
        </h3>
        <ul className="list-disc pl-5 mb-4 space-y-2" data-id="element-2034" data-mp="33ohd2aq9ldrd">
          <li data-id="element-2035" data-mp="33mwjanbrxyuk">
            <strong data-id="element-2036" data-mp="33mmv95aai1ri">Anything that has to drain.</strong> French drains,
            downspout trenches, pipe bedding, dry creek beds. The fines are the
            enemy here — use{' '}
            <Link to="/shop/7-8-drain-rock" className="underline underline-offset-2" data-id="element-2037" data-mp="3dgvh5a4tewgp">
              7/8" Drain Rock
            </Link>{' '}
            instead.
          </li>
          <li data-id="element-2038" data-mp="3mg0ggio7attx">
            <strong data-id="element-2039" data-mp="3mfqoipjlicll">Loaded-truck traffic.</strong> Dump trucks, equipment
            trailers, or a commercial approach want a coarser{' '}
            <Link to="/shop/1-25-minus" className="underline underline-offset-2" data-id="element-2040" data-mp="16qs9mno27umv">
              1.25" Minus
            </Link>{' '}
            base under them.
          </li>
          <li data-id="element-2041" data-mp="11ta5rbowg5lq">
            <strong data-id="element-2042" data-mp="wsm5dzes6ceu">Soft clay with no base.</strong> Putting your wearing surface
            straight onto clay just pumps rock down into it. Build 4 inches of
            1.25" Minus first, then top with 5/8".
          </li>
          <li data-id="element-2043" data-mp="1pgrroqlhg9gs">
            <strong data-id="element-2044" data-mp="1pickobs5fkcj">Steep grades.</strong> Past roughly 15%, winter runoff walks
            the gravel downhill no matter how well you compacted it. That is a
            paving or chip-seal conversation.
          </li>
        </ul>

        <img src="https://cdn.magicpatterns.com/uploads/oevLQWcfQc8d828rBxgJqr/gravel_delivery_in_tacoma.png" alt="206Gravel truck delivering 5/8 minus gravel in a Tacoma alley" className="w-full h-64 md:h-80 object-cover rounded-xl my-6" data-id="element-2045" data-mp="1fp45p57p3kmp" />

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2046" data-mp="1sxml0zkzq6bx">
          Installing It Properly
        </h3>
        <ol className="list-decimal pl-5 mb-4 space-y-3" data-id="element-2047" data-mp="1x1111q8ximpd">
          <li data-id="element-2048" data-mp="1j0828tblh5gg">
            <strong data-id="element-2049" data-mp="1j0qaypfg1c59">Get the sub-grade firm and shaped first.</strong> Strip sod
            and soft organics, then grade the crown before any rock lands. You
            cannot fix a bowl-shaped sub-grade by burying it — the low spot
            stays a low spot and holds water under your new surface.
          </li>
          <li data-id="element-2050" data-mp="2gmxd7so9yseg">
            <strong data-id="element-2051" data-mp="2gmnp6amsivbe">Lay commercial-grade fabric.</strong> Not the thin weed
            barrier from the garden aisle. Overlap seams 6–12 inches. Fabric is
            what stops your rock from migrating down into the soil, which is the
            actual failure mode on most Puget Sound driveways.
          </li>
          <li data-id="element-2052" data-mp="2ulexthivvos5">
            <strong data-id="element-2053" data-mp="2zdycyix7yrz2">Spread in lifts, not one deep pile.</strong> Maximum 3–4
            inches per pass. A single 8-inch dump compacts on top and stays
            loose underneath, and it will rut the first time something heavy
            parks on it.
          </li>
          <li data-id="element-2054" data-mp="izmkwb3fgbu3">
            <strong data-id="element-2055" data-mp="iy1xd5fj5t5q">Build a real crown.</strong> Center about 2 inches higher
            than the edges across a 10-foot width. Water has to leave sideways;
            if it runs the length of the drive it cuts channels.
          </li>
          <li data-id="element-2056" data-mp="4zbqiy2wsf46">
            <strong data-id="element-2057" data-mp="51512ogn7jhg">Compact damp.</strong> A vibratory plate compactor, several
            passes per lift, with the material slightly moist. Bone-dry fines
            never bind; saturated fines turn to slurry. Damp is the window, and
            a garden hose on mist gets you there.
          </li>
        </ol>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2058" data-mp="905f7to6dxy4">
          The Four Mistakes That Cost People a Second Delivery
        </h3>
        <ul className="list-disc pl-5 mb-4 space-y-2" data-id="element-2059" data-mp="1khn9m2jv9l7z">
          <li data-id="element-2060" data-mp="1kiycm05hmoy1">
            <strong data-id="element-2061" data-mp="i3fx8ybdjpmw">Ordering clean instead of minus.</strong> The surface never
            firms up and tires push it into windrows within a month.
          </li>
          <li data-id="element-2062" data-mp="n0zca0fwc6fw">
            <strong data-id="element-2063" data-mp="n4n5eyhfiqu3">Skipping compaction entirely.</strong> Loose minus is just
            expensive gravel. Compaction is what converts it into a surface.
          </li>
          <li data-id="element-2064" data-mp="w1xjsspjmw38">
            <strong data-id="element-2065" data-mp="w2fnubig6elp">Filling potholes without squaring them.</strong> Rock dumped
            into a smooth dish works straight back out. Cut the edges square
            first so the patch has something to key into.
          </li>
          <li data-id="element-2066" data-mp="8dmo03ypfmlt">
            <strong data-id="element-2067" data-mp="8dwc1m06vjov">Treating a water problem as a gravel problem.</strong> If
            street runoff or a downspout crosses the driveway, no amount of new
            rock holds. Fix the drainage, then re-rock.
          </li>
        </ul>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2068" data-mp="3btla45a1xqd2">
          What It Costs Delivered
        </h3>
        <p className="mb-4" data-id="element-2069" data-mp="3gr3dlg5avddo">
          5/8" Minus is $65 per yard. Delivery is a flat fee per truckload by
          zone — $80, $120, or $175 depending on distance — and it is the same
          fee whether you take 3 yards or a full 12. That is why partial loads
          are the expensive way to buy rock: a 12-yard load to Kent works out to
          about $72 per yard delivered, while a 4-yard load to the same address
          is closer to $85.
        </p>
        <p className="mb-4" data-id="element-2070" data-mp="2qcnizd2rz0n6">
          If you are within a load of a full truck, it is almost always worth
          rounding up. Enter your address on the{' '}
          <Link to="/shop/5-8-minus" className="underline underline-offset-2" data-id="element-2071" data-mp="2gkq6m30162mv">
            product page
          </Link>{' '}
          and you will see the real delivered total before you order — or text
          dispatch your dimensions and we will work the yardage out with you.
        </p>
      </div>
}, {
  slug: 'best-time-regrade-gravel-driveway-seattle',
  title: 'August Is the Best Month to Regrade a Gravel Driveway in Seattle',
  date: 'August 1, 2026',
  excerpt: 'Puget Sound gets roughly six dry weeks a year, and August is the heart of them. Here is why late summer is the only window where crushed rock actually compacts — and what happens if you wait until the rain returns.',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/9f693654-2a38-443b-b05e-cbf8adf96a42.jpg',
  type: 'article',
  content: <div className="prose dark:prose-invert max-w-none text-brand-charcoal dark:text-stone-300" data-id="element-2072" data-mp="37qwuq6u4c50s">
        <p className="mb-4" data-id="element-2073" data-mp="3cj6qw4ltu1bi">
          Every fall we get the same call: a driveway in Shoreline or Renton
          that turned to soup after the first serious rain, and the homeowner
          wants gravel delivered tomorrow. We can absolutely deliver it. But the
          honest answer is that the repair would have cost less, gone faster,
          and lasted years longer if it had happened in August.
        </p>
        <p className="mb-4" data-id="element-2074" data-mp="2tr44ecmi7qtc">
          Seattle averages under an inch of rain in August and close to six
          inches by November. That difference is not just a comfort issue for
          whoever is running the plate compactor — it decides whether your new
          rock locks together or floats.
        </p>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2075" data-mp="2j4h1i5j4rgvd">
          Why Dry Ground Is the Whole Game
        </h3>
        <p className="mb-4" data-id="element-2076" data-mp="2t47zih6wge07">
          Crushed minus rock compacts because the stone dust in it fills the
          voids between the angular pieces and binds them into a near-solid
          slab. That only works within a fairly narrow moisture range. Too dry
          and the fines never bind; far more common around here, too wet and the
          sub-grade underneath turns plastic. The compactor pushes rock down
          into mud instead of knitting it together, and you have effectively
          buried a few hundred dollars of material.
        </p>
        <p className="mb-4" data-id="element-2077" data-mp="3jf4dw2r93hf3">
          In August the native glacial till under most Puget Sound driveways is
          as firm as it gets all year. You compact onto something solid, the
          layers stay where you put them, and the finished surface has two full
          months of dry weather to settle under traffic before the rain tests
          it.
        </p>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2078" data-mp="3of0hw1zy78un">
          What a Late-Summer Regrade Actually Involves
        </h3>
        <ul className="list-disc pl-5 mb-4 space-y-2" data-id="element-2079" data-mp="2vqbe1v7icpf7">
          <li data-id="element-2080" data-mp="2vngwcbfvpiph">
            <strong data-id="element-2081" data-mp="3aeg0na9oll39">Re-establish the crown.</strong> The center should sit
            roughly 2 inches higher than the edges over a 10-foot width so water
            sheds sideways instead of running the length of the drive and
            cutting channels.
          </li>
          <li data-id="element-2082" data-mp="2hwakrgzbrum8">
            <strong data-id="element-2083" data-mp="2hvskm99jkw91">Fix the potholes properly.</strong> Square up the edges of
            each hole rather than just filling the dish — loose rock dumped into
            a smooth bowl works right back out within a season.
          </li>
          <li data-id="element-2084" data-mp="x7zuspdknvz3">
            <strong data-id="element-2085" data-mp="x8i0ecm4jmjo">Top with 5/8" Minus.</strong> Two to three inches of fresh
            surface rock, compacted in a single pass, restores the driving
            surface without rebuilding the base.
          </li>
          <li data-id="element-2086" data-mp="nig8taukbm00">
            <strong data-id="element-2087" data-mp="nfu6bpmj3won">Check where the water goes.</strong> If runoff from the
            street or a downspout crosses the driveway, no amount of new rock
            will hold. That is a drainage fix, not a gravel fix.
          </li>
        </ul>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2088" data-mp="127m8ezxbbe8f">
          If the Base Is Gone, Do It Now
        </h3>
        <p className="mb-4" data-id="element-2089" data-mp="1742xbwcc5wmk">
          Surface rutting is a topcoat problem. But if you can feel the driveway
          flex under a vehicle, or ruts return within weeks of being filled, the
          structural base has failed and topping it is throwing good money
          after bad. Rebuilding means 3–4 inches of 1.25" Minus compacted as a
          base, then the 5/8" Minus surface over it. That is a job you want on
          dry ground — attempting it in November roughly doubles the material it
          takes to reach a stable grade.
        </p>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2090" data-mp="1jpb8ww4uim6z">
          One Local Wrinkle: Drainage Rock Season
        </h3>
        <p className="mb-4" data-id="element-2091" data-mp="1enc5xnd0prak">
          August is also the practical deadline for French drains and downspout
          trenches. Digging is dramatically easier in dry soil, and a drain
          installed now is proven before the wet season instead of during it. If
          your driveway problem is really a water problem, 7/8" Drain Rock in a
          properly fabric-wrapped trench will do more for it than any amount of
          surface gravel.
        </p>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2092" data-mp="2v4zy7u5knxay">
          The Short Version
        </h3>
        <p className="mb-4" data-id="element-2093" data-mp="gttadrogsrew">
          You have until roughly late September before Puget Sound weather stops
          cooperating. A driveway regraded in August compacts properly, settles
          under two months of dry traffic, and heads into winter with a crown
          that actually sheds water. The same work in November costs more rock
          and delivers a worse result. If you are on the fence, this is the
          month to get it done — call or text dispatch and we will help you
          figure out the yardage before you order.
        </p>
      </div>
}, {
  slug: 'walkway-gravel-vs-driveway-gravel',
  title: 'Walkway Gravel vs Driveway Gravel — Which One Do I Need?',
  date: 'June 26, 2026',
  excerpt: 'They look similar in the bin, but walkway gravel and driveway gravel are built for very different jobs. Here is how to pick the right one so your project lasts.',
  image: 'https://cdn.magicpatterns.com/uploads/ebxsCGdnf9doSY72o8VZYu/file_0000000013d071fd98aa813ad564b1e6.png',
  type: 'article',
  content: <div className="prose dark:prose-invert max-w-none text-brand-charcoal dark:text-stone-300" data-id="element-2094" data-mp="1gvzhznbwyhtb">
        <p className="mb-4" data-id="element-2095" data-mp="1uv904sjojlzx">
          Walkway gravel and driveway gravel can look almost identical sitting
          in a bulk bin, but they are engineered for completely different loads.
          A path only has to carry foot traffic; a driveway has to hold up under
          the weight of cars, trucks, and the occasional delivery vehicle.
          Choosing the wrong one means a soft, rutted path or a driveway that
          scatters every time you back out. Here is how to tell them apart.
        </p>

        <img src="https://cdn.magicpatterns.com/uploads/wghDVQbBxatajKiUKq9jcv/driveway_gravel_spreading_in_tacoma.png" alt="Two workers raking crushed gravel behind a 206Gravel delivery truck on a driveway" className="w-full h-64 md:h-80 object-cover rounded-xl my-6" data-id="element-2096" data-mp="29k52qa2yw6t5" />

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2097" data-mp="326vcuj3ad5s7">
          Walkway Gravel: Comfort & Looks
        </h3>
        <p className="mb-4" data-id="element-2098" data-mp="374wseu1i6myx">
          Walkways prioritize a comfortable surface to walk on and a clean,
          finished appearance. Smaller, rounded, or decorative stone — like 3/8"
          pea gravel or crushed drain rock — feels good underfoot and packs into
          a stable, attractive path. Because a walkway never sees vehicle
          weight, you do not need a heavy structural base; a few inches of
          material over a compacted, weed-blocked sub-grade is usually plenty.
        </p>
        <ul className="list-disc pl-5 mb-4 space-y-2" data-id="element-2099" data-mp="29jink15qqjv7">
          <li data-id="element-2100" data-mp="2eh1164updn9j">
            <strong data-id="element-2101" data-mp="2egiwcjtz60q0">Stone size:</strong> 3/8" pea gravel or small decorative
            rock — smooth and easy on bare feet.
          </li>
          <li data-id="element-2102" data-mp="20ib4gqbspplp">
            <strong data-id="element-2103" data-mp="20jm5wjhrql9n">Depth:</strong> 2–3 inches over landscape fabric.
          </li>
          <li data-id="element-2104" data-mp="25ft41q3xash1">
            <strong data-id="element-2105" data-mp="25ip53nb2hx9p">Best for:</strong> garden paths, side yards, patio borders,
            and fire-pit surrounds.
          </li>
        </ul>

        {/* Replace this image later */}
        <img src="https://cdn.magicpatterns.com/uploads/9QXpjprgL7K5DjWViDEHz9/spreading_the_fabric_driveway.png" alt="Landscape fabric laid down before a gravel walkway" className="w-full h-64 md:h-80 object-cover rounded-xl my-6" data-id="element-2106" data-mp="68cnmf35hma3" />

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2107" data-mp="tz9sx90fxlcd">
          Driveway Gravel: Strength & Stability
        </h3>
        <p className="mb-4" data-id="element-2108" data-mp="p1gho1ka6158">
          Driveways need angular, "minus" crushed rock that locks together under
          weight. The fines (stone dust) in a minus product bind the larger
          stones into a firm, near-solid surface once compacted — exactly what
          you want when a vehicle rolls over it. A driveway is built in layers:
          a coarse 1.25" Minus base for structure, topped with a smoother 5/8"
          Minus driving surface that sheds water and resists potholes.
        </p>
        <ul className="list-disc pl-5 mb-4 space-y-2" data-id="element-2109" data-mp="1g98eo7y5956v">
          <li data-id="element-2110" data-mp="1l68lil2tibz1">
            <strong data-id="element-2111" data-mp="1l7tei69hhmus">Base layer:</strong> 1.25" Minus crushed rock, 3–4 inches,
            compacted.
          </li>
          <li data-id="element-2112" data-mp="1p7k2k27mlovm">
            <strong data-id="element-2113" data-mp="1p729feg9fqrx">Top layer:</strong> 5/8" Minus crushed rock, 2–3 inches, the
            finished driving surface.
          </li>
          <li data-id="element-2114" data-mp="1yy5869lpvto1">
            <strong data-id="element-2115" data-mp="1yyogqzph95pe">Best for:</strong> driveways, parking pads, and any surface
            that carries vehicles.
          </li>
        </ul>

        {/* Replace this image later */}
        <img src="https://cdn.magicpatterns.com/uploads/7Nv3L8waV4wtGihPojgZkN/fina_product_gravel_driveway_in_tacoma.png" alt="Finished compacted gravel driveway" className="w-full h-64 md:h-80 object-cover rounded-xl my-6" data-id="element-2116" data-mp="28wcl8hcvxrkr" />

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2117" data-mp="ej3c4qo94831">
          Quick Answer
        </h3>
        <p className="mb-4" data-id="element-2118" data-mp="ikf24xb1evbr">
          If people walk on it, choose a smaller, comfortable stone like 3/8"
          pea gravel. If vehicles drive on it, use angular minus crushed rock in
          two compacted layers. When in doubt, give us a call — we will help you
          pick the right material and the right amount before you order.
        </p>
      </div>
}, {
  slug: 'perfect-gravel-driveway',
  title: 'How to Build a Perfect Gravel Driveway',
  date: 'May 24, 2026',
  excerpt: 'A complete step-by-step guide to transforming a dirt driveway into a professional, long-lasting gravel surface. No contractor needed.',
  image: 'https://cdn.magicpatterns.com/uploads/bH95N6JcLS3ddKehbnVRWv/gravel_landscaping_in_tacoma.png',
  type: 'guide',
  quickInfo: ['⏱ Weekend project', '📏 Any driveway size', '💰 ~$3–5/sq ft', '🔧 Basic tools needed'],
  materialsNeeded: [{
    name: '1.25" Minus Crushed Rock',
    use: 'Base layer — compacts into a solid foundation',
    slug: '1-25-minus'
  }, {
    name: '5/8" Minus Crushed Rock',
    use: 'Top layer — smooth, firm driving surface',
    slug: '5-8-minus'
  }, {
    name: 'Landscape Fabric',
    use: 'Weed barrier between soil and gravel',
    slug: null
  }, {
    name: 'Fill Dirt (if needed)',
    use: 'Level out low spots before gravel',
    slug: null
  }],
  steps: [{
    number: 1,
    title: 'Assess & Measure Your Driveway',
    image: 'https://cdn.magicpatterns.com/uploads/2ebE9ciuiVZZKZHNZLJiZ5/measuring_gravel_driveway.png',
    description: 'Start by measuring the length, width, and noting the current condition of your dirt driveway. Mark the boundaries with stakes and string. Calculate the square footage — this determines how much material you need.',
    tips: ['Measure length × width in feet', 'Plan for 4–6 inches of base material depth', 'Use our Calculator to estimate yards needed', 'Account for any slope or drainage needs']
  }, {
    number: 2,
    title: 'Clear & Grade the Surface',
    image: 'https://cdn.magicpatterns.com/uploads/gQYsAvdSYDiWUkNHGFHmQH/grading_driveway.png',
    description: 'Remove any vegetation, large rocks, and debris from the driveway area. Use a rake or skid steer to grade the surface so water drains to the sides. A slight crown (higher in the center) prevents puddles.',
    tips: ['Remove all grass, weeds, and roots', 'Create a 2% slope from center to edges', 'Fill any low spots with fill dirt', 'Compact the bare soil with a plate compactor']
  }, {
    number: 3,
    title: 'Install Landscape Fabric (Optional)',
    image: 'https://cdn.magicpatterns.com/uploads/9QXpjprgL7K5DjWViDEHz9/spreading_the_fabric_driveway.png',
    description: 'Lay commercial-grade landscape fabric over the graded dirt. This prevents weeds from growing up through your gravel and helps separate the soil from the rock layers. Overlap seams by 6–12 inches.',
    tips: ['Use commercial-grade fabric, not cheap weed barrier', 'Overlap seams by at least 6 inches', 'Secure with landscape staples every 3 feet', 'This step saves major headaches down the road']
  }, {
    number: 4,
    title: 'Spread the Base Layer — 1.25" Minus',
    image: 'https://cdn.magicpatterns.com/uploads/wghDVQbBxatajKiUKq9jcv/driveway_gravel_spreading_in_tacoma.png',
    description: 'The base layer is the foundation of your driveway. Spread 3–4 inches of 1.25" Minus crushed rock evenly across the surface. This coarser material locks together and creates a rock-solid base that won\'t shift.',
    tips: ['Order 1.25" Minus from our catalog', 'Spread evenly with a rake to 3–4" depth', 'Compact thoroughly with a plate compactor', 'Make multiple passes until the surface is firm'],
    material: '1.25" Minus',
    materialSlug: '1-25-minus'
  }, {
    number: 5,
    title: 'Compact the Base Layer',
    image: 'https://cdn.magicpatterns.com/uploads/hkbT29dFvc9BT49e5MH1GV/compacting_base_layer.png',
    description: 'Rent a plate compactor (available at most equipment rental shops) and make several passes over the base layer. Wet the material lightly before compacting — moisture helps the fines bind together for maximum density.',
    tips: ['Lightly wet the gravel before compacting', 'Make 3–4 passes in different directions', 'The surface should feel solid underfoot', 'Add more material to any low spots and re-compact']
  }, {
    number: 6,
    title: 'Add the Top Layer — 5/8" Minus',
    image: 'https://cdn.magicpatterns.com/uploads/pqds5pqkfBofDzHJ4pjSBj/base_layer_compacting.png',
    description: 'Now spread 2–3 inches of 5/8" Minus crushed rock on top. This is the finished driving surface — it packs down tight, sheds water, and provides a smooth, firm surface for vehicles and foot traffic.',
    tips: ['Order 5/8" Minus — our most popular driveway gravel', 'Spread to 2–3" depth over the compacted base', 'Rake smooth and maintain the crown shape', 'Compact one final time for a professional finish'],
    material: '5/8" Minus',
    materialSlug: '5-8-minus'
  }, {
    number: 7,
    title: 'Final Compaction & Edging',
    image: 'https://cdn.magicpatterns.com/uploads/7Nv3L8waV4wtGihPojgZkN/fina_product_gravel_driveway_in_tacoma.png',
    description: 'Give the top layer a final compaction pass. Optionally, install edging (timber, steel, or stone) along the sides to keep gravel contained. Your new driveway is ready to use — it will continue to settle and harden over the first few weeks.',
    tips: ['Edging prevents gravel from migrating into the yard', 'Drive on it gently for the first week', 'Top off with a thin layer of 5/8" Minus annually', 'Keep drainage channels clear of debris']
  }],
  seoBlocks: [{
    title: 'Gravel Delivery Across Pierce & King Counties',
    content: <>
            <p className="text-brand-charcoal/80 dark:text-stone-300 text-sm leading-relaxed mb-3" data-id="element-2119" data-mp="2ahvhj1cfk4zh">
              Whether you're building a new gravel driveway in Puyallup,
              replacing a muddy parking pad in Bonney Lake, or resurfacing a
              rural road in Enumclaw, having the right materials delivered on
              time makes all the difference. 206Gravel provides same-day and
              next-day bulk gravel delivery from Tacoma to Seattle and
              everywhere in between — Lakewood, Federal Way, Auburn, Kent,
              Sumner, Edgewood, Renton, Bellevue, Kirkland, Redmond, and the
              surrounding Pierce and King County communities.
            </p>
            <p className="text-brand-charcoal/80 dark:text-stone-300 text-sm leading-relaxed" data-id="element-2120" data-mp="25dumqtm6ufuj">
              Our dump trucks carry 5–12 yards per load and can place material
              directly on your driveway, jobsite, or backyard — no wheelbarrow
              relay required. We know the local soil conditions (hello, Puget
              Sound clay) and can recommend the right base and top layers for
              driveways that last through Pacific Northwest winters.
            </p>
          </>
  }, {
    title: 'Why Buy Gravel Locally in the Puget Sound?',
    content: <>
            <p className="text-brand-charcoal/80 dark:text-stone-300 text-sm leading-relaxed mb-3" data-id="element-2121" data-mp="tyyx6cm06tz7">
              Big-box stores sell gravel by the bag — but for driveways and
              serious landscaping, you need bulk delivery by the yard. Buying
              locally from 206Gravel means lower delivery costs, fresher
              material sourced from regional quarries, and drivers who know how
              to navigate Tacoma's hills, Seattle's tight streets, King County's
              rural roads, and everything in between.
            </p>
            <p className="text-brand-charcoal/80 dark:text-stone-300 text-sm leading-relaxed" data-id="element-2122" data-mp="3m454b6ho48xy">
              We're based in the 206 area code and deliver to King County and
              King County daily. No middlemen, no markups from national chains —
              just honest pricing on crushed rock, drain rock, topsoil, bark,
              and recycled aggregates delivered straight to your property.
            </p>
          </>
  }]
}, {
  slug: 'best-base-for-gravel-driveway',
  title: 'Best Base for a Gravel Driveway',
  date: 'May 19, 2026',
  excerpt: 'The best base for a gravel driveway is a large angular crushed stone (around 1 to 2 inches) for the sub-base, laid...',
  image: 'https://cdn.magicpatterns.com/uploads/8BfgzDv9jVyH7TX3BiStiF/Gravel2BDriveway2BHero2BDesktop.webp',
  type: 'article',
  content: <div className="prose dark:prose-invert max-w-none text-brand-charcoal dark:text-stone-300" data-id="element-2123" data-mp="1y5b3kljaths0">
        <p className="mb-4" data-id="element-2124" data-mp="1jgy95szv06zm">
          The foundation of any long-lasting gravel driveway is its base layer.
          Without a proper base, your top gravel will sink into the mud,
          creating potholes and requiring constant maintenance.
        </p>
        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2125" data-mp="1eidfydj0ynko">
          Why 1.25" Minus is the Standard
        </h3>
        <p className="mb-4" data-id="element-2126" data-mp="10jdu1dmwcms7">
          For most residential driveways in the Pacific Northwest, 1.25" Minus
          crushed rock is the go-to base material. The "minus" means it contains
          stone dust (fines) along with the larger 1.25-inch angular rocks. When
          compacted, these fines lock the larger stones together like concrete.
        </p>
        <p className="mb-4" data-id="element-2127" data-mp="vuvzsxdkn37z">
          Always ensure you lay down at least 3-4 inches of base rock and
          compact it thoroughly before adding your top layer.
        </p>
      </div>
}, {
  slug: 'how-much-is-a-load-of-gravel',
  title: 'How Much is a Load of Gravel?',
  date: 'May 11, 2026',
  excerpt: 'A standard tandem-axle dump truck load of gravel (around 12 to 15 tons) costs around $400 to $1,000 delivered in 2026...',
  image: 'https://cdn.magicpatterns.com/uploads/pPaGG3aiENovvC8wi26rwz/TJHunt5-700x467.jpg',
  type: 'article',
  content: <div className="prose dark:prose-invert max-w-none text-brand-charcoal dark:text-stone-300" data-id="element-2128" data-mp="19mj5fntelo3f">
        <p className="mb-4" data-id="element-2129" data-mp="1nlsb3tjzgzt5">
          The cost of a load of gravel depends heavily on the material type,
          your location, and the size of the truck delivering it.
        </p>
        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2130" data-mp="2pyd7i0ilyju4">
          Truck Sizes
        </h3>
        <ul className="list-disc pl-5 mb-4 space-y-2" data-id="element-2131" data-mp="2v02khlpe57ku">
          <li data-id="element-2132" data-mp="2zy45ic6dngyy">
            <strong data-id="element-2133" data-mp="2zxkl8ot98ghr">Solo Dump Truck:</strong> Carries about 5 to 7 yards (6-9
            tons). Ideal for smaller residential projects.
          </li>
          <li data-id="element-2134" data-mp="278ne9huhu64b">
            <strong data-id="element-2135" data-mp="278x3v4bmmb9h">Tandem-Axle Dump Truck:</strong> Carries about 10 to 12
            yards (12-15 tons). The standard for most driveway installations.
          </li>
          <li data-id="element-2136" data-mp="2c540g6i4uaer">
            <strong data-id="element-2137" data-mp="2g9bsacjw06h7">Truck and Pup:</strong> Carries up to 24 yards. Best for
            large rural driveways or commercial jobs.
          </li>
        </ul>
        <p className="mb-4" data-id="element-2138" data-mp="2l13anzsw35w9">
          Remember that delivery fees are often a significant portion of the
          total cost, which is why buying locally from a supplier in your
          delivery zone is crucial.
        </p>
      </div>
}];